import { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext.tsx';

type FetchState<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string };

export function useFetch<T>(path: string) {
  const { api, logout } = useAuth();
  const [state, setState] = useState<FetchState<T>>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await api(path);
        if (res.status === 401) {
          await logout();
          return;
        }
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: res.statusText }));
          throw new Error(err.detail || res.statusText);
        }
        const data = (await res.json()) as T;
        if (!cancelled) setState({ status: 'ok', data });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [path, api, logout]);

  return state;
}
